import React, {useEffect} from 'react';
import {useLoginMutation, useShopifyLoginMutation} from 'services/auth'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {login as loginStore} from 'store/authSlice'
import {useAppDispatch} from 'store/hooks'
import {jwtDecode} from 'jwt-decode'
import Button from 'components/ui/Button'

interface IJwtDecoded {
    aud: string
    auth_time: number
    ['cognito:username']: string
    ['custom:type']: string
    email: string
    email_verified: boolean
    event_id: string
    exp: number
    iat: number
    iss: string
    jti: string
    name: string
    origin_jti: string
    phone_number_verified: boolean
    sub: string
    token_use: string
}

const Login = () => {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams();
    const [login, {isLoading}] = useLoginMutation()
    const [shopifyLogin, {isLoading: isLoadingShopifyLogin}] = useShopifyLoginMutation()

    const dispatch = useAppDispatch()

    const processLogin = (token: string) => {
        const tokenDecoded: any = jwtDecode(token)
        const userInfo: IJwtDecoded = tokenDecoded
        const userType = userInfo['custom:type']
        const userId = userInfo['cognito:username']
        const customerId = userInfo['cognito:username']

        dispatch(
            loginStore({
                user: {
                    email: userInfo.email,
                    id: userId,
                    type: userType,
                    first_name: userInfo.name
                },
                token,
                merchantId: userId,
                merchantName: userInfo.name,
                customerId
            })
        )

        const dashboardRoute = '/admin/dashboard'
        navigate(dashboardRoute)
    }

    useEffect(() => {
        const shop = searchParams.get('shop');
        const token = searchParams.get('token');

        // Debug logging for URL parameters
        console.log('🔍 Login useEffect - URL Parameters:', {
            shop,
            token: token ? `${token.substring(0, 10)}...` : null,
            fullUrl: window.location.href,
            searchParams: Object.fromEntries(searchParams.entries())
        });

        // Debug logging for environment variables
        console.log('🔍 Environment Variables:', {
            REACT_APP_SUREFIRE_API: process.env.REACT_APP_SUREFIRE_API,
            REACT_APP_SHOPIFY_URL: process.env.REACT_APP_SHOPIFY_URL,
            NODE_ENV: process.env.NODE_ENV
        });

        const autoLogin = async () => {
            if (!shop || !token) {
                console.log('❌ Auto-login skipped - missing parameters:', { shop, hasToken: !!token });
                return
            }

            console.log('🚀 Starting auto-login process...');
            console.log('📤 Request payload:', { shop, token: `${token.substring(0, 10)}...` });

            try {
                console.log('📡 Making shopifyLogin API call...');
                const response: any = await shopifyLogin({shop, token});
                
                console.log('✅ shopifyLogin response received:', {
                    status: response?.meta?.response?.status,
                    statusText: response?.meta?.response?.statusText,
                    hasData: !!response?.data,
                    dataKeys: response?.data ? Object.keys(response.data) : null,
                    fullResponse: response
                });

                if (response.data) {
                    console.log('🎉 Processing successful login response');
                    processLogin(response.data.token);
                } else {
                    console.warn('⚠️ Response received but no data found:', response);
                }
            } catch (error: any) {
                console.error('❌ Error during auto-login:', {
                    message: error?.message,
                    status: error?.status,
                    statusText: error?.statusText,
                    data: error?.data,
                    originalError: error
                });

                // Log additional error details if available
                if (error?.data) {
                    console.error('📋 Error response data:', error.data);
                }
                
                if (error?.meta?.response) {
                    console.error('📋 Error response meta:', {
                        status: error.meta.response.status,
                        statusText: error.meta.response.statusText,
                        headers: error.meta.response.headers,
                        url: error.meta.response.url
                    });
                }
            }
        };

        autoLogin();
    }, [searchParams]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        const formData = new FormData(event.currentTarget)
        const formDataObject = Object.fromEntries(formData.entries())
        const response: any = await login(formDataObject)

        if (response.data) {
            processLogin(response.data.token);
        }
    }

    if (isLoadingShopifyLogin) {
        return (
            <div className='flex justify-center items-center'>
                Loading...
            </div>
        )
    }

    return (
        <div data-testid="module-login-merchant">
            {/*<LoginForm*/}
            {/*  formId="component-forms-login-merchant"*/}
            {/*  title="Log in"*/}
            {/*  buttonText="Sign in"*/}
            {/*  isLoading={isLoading}*/}
            {/*  handleSubmit={handleSubmit}*/}
            {/*/>*/}
            <div style={{paddingBottom: 20}}>Your login expired, please navigate back to the Portal app on Shopify to
                login!
            </div>
            <Button cc-color="purple" onClick={() => window.open(`${process.env.REACT_APP_SHOPIFY_URL}`)}>
                Go to back to Shopify
            </Button>
        </div>
    )
}

export default Login
